export const en = {
  common: {
    learnMore: "Learn more",
    viewAll: "View all",
    contactUs: "Get in touch",
    bookCall: "Book a call",
    seePricing: "See pricing",
    requestProject: "Request a project",
    freeIntro: "Free intro call",
    askAi: "Ask AI",
    openAi: "Ask AI",
    openAiOverlayAria: "Open AI overlay",
    backHome: "Back to homepage",
    yes: "Yes",
    no: "No",
    loading: "Loading prices.",
  },
  nav: {
    products: "Products",
    about: "About",
    pricing: "Pricing",
    contact: "Contact",
  },
  home: {
    hero: {
      title: "AI layer, rework and advisory for your website.",
      subtitle:
        "We build a context-aware AI layer on your existing site, rework whole websites to be AI-native, and stay with you long term. Direct collaboration with the people who also write the code.",
    },
    products: {
      title: "What we do.",
      lead: "Three core offerings, built for direct, long-term collaboration. You talk to the people who also code. No account-manager layer in between.",
      moreHeading: "More projects & products",
      moreLink: "View all products",
      services: {
        overlay: {
          name: "AI Overlay",
          tagline: "AI layer on your existing website.",
          desc: "A context-aware AI layer, trained on your content, just like the one on this page. Answers questions, generates fitting interfaces live, and guides visitors to your content. No re-launch, integrated in 1 to 2 weeks.",
          bullets: [
            "From €29/month on a 12-month term",
            "Setup free if you do it yourself",
            "Trained on your content",
          ],
        },
        rework: {
          name: "Website Rework, AI First",
          tagline: "Rethink existing sites as AI-native.",
          desc: "We redesign your website with AI as the central interaction, not as a bolted-on chatbot. Conversion-relevant paths, generative content, clear architecture. Fixed price, fixed phases, predictable launch.",
          bullets: [
            "Fixed price per phase",
            "Design and engineering from one team",
            "Live prototype after 1 to 2 weeks",
          ],
        },
        longterm: {
          name: "Long-term Partnership",
          tagline: "Direct, long-term collaboration.",
          desc: "Advisory, maintenance and continued development with the same people who built it. You talk to devs, not account managers. Monthly iteration, clear roadmap, predictable effort.",
          bullets: [
            "Direct line to devs",
            "Monthly iteration",
            "Advisory included",
          ],
        },
      },
      moreProducts: {
        cate: "Infinite canvas IDE for coding agents",
        "one-click-deploy": "Deploy containers to your servers",
        sight: "SEO and AI visibility audits",
        skyline: "iOS app for discovering buildings",
        foody: "iOS app for pantry and recipes",
      },
    },
    why: {
      title: "The web is being rethought.",
      titleAccent: " We're helping build it.",
      lead: "The next generation of web software won't be augmented by AI, it will be defined by it. From that follow three principles by which we measure everything we build.",
      principles: [
        {
          n: "01",
          title: "AI as interface",
          body: "Every serious web application will gain a native AI layer in the next few years. Not as a bolted-on chatbot, but as the central form of interaction.",
        },
        {
          n: "02",
          title: "Open source by default",
          body: "What we build should work without us. Our tools are MIT-licensed. No lock-in, no black box, no vendor hostage.",
        },
        {
          n: "03",
          title: "Production over pitch",
          body: "We build software that gets deployed, not demos for slides. Stable stacks, transparent cost, predictable operations. What really matters is always the boring stuff.",
        },
      ],
    },
    how: {
      title: "Four steps.",
      titleAccent: " No surprises.",
      lead: "Fixed prices for clearly bounded phases. From day one you know what it costs and what you get when.",
      steps: [
        {
          n: "01",
          label: "Discovery",
          title: "Intro call & scope",
          body: "Free 60-minute call. We clarify use case, data, budget, and whether AI is even the right lever.",
        },
        {
          n: "02",
          label: "Prototype",
          title: "1 to 2 weeks, fixed price",
          body: "Working prototype on a real stack. You see early how it feels, before the full budget is committed.",
        },
        {
          n: "03",
          label: "Build",
          title: "Engineering & design",
          body: "Incremental delivery in 2-week iterations. Frontend, backend, AI layer and infrastructure from one team.",
        },
        {
          n: "04",
          label: "Operate",
          title: "Maintenance & continued development",
          body: "Optional maintenance contract. We help operate, keep models current, and continue building along the roadmap.",
        },
      ],
    },
    who: {
      title: "0-AI UG. Small studio, clear position.",
      lead: "We're a studio for AI-native web software, based in Germany. Small, focused, with our own products as a foundation. You work directly with the people who also write the code.",
      stats: [
        { n: "5+", label: "Own products" },
        { n: "10+", label: "Years of web engineering" },
        { n: "100%", label: "Open source share, wherever possible" },
      ],
      links: {
        about: "About",
        opensource: "Open source",
        work: "Case studies",
      },
    },
    pricing: {
      title: "Three packages. Fixed price.",
      lead: "Maintenance and AI layer optionally combinable. Full prices, discounts and examples on the pricing page.",
      popular: "Popular",
      onRequest: "On request",
      onRequestNote: "individual quote",
      from: "from €",
      onceTax: "one-off, plus VAT.",
      details: "All details",
      fullPricing: "Full pricing overview",
    },
    cta: {
      title: "Let's talk about your project.",
      lead: "Free 60-minute intro call. We figure out together whether and how AI makes sense for what you have in mind. No commitment, no sales pitch.",
      bookConsulting: "Book advisory",
    },
    cate: {
      eyebrow: "Our product",
      title: "CATE. The IDE as an infinite canvas.",
      lead: "Run parallel coding agents on a zoomable canvas. Every terminal shows whether its agent is working, finished or waiting. Open source, MIT-licensed.",
      bullets: [
        "Agent chat with Codex, Claude Code, Cursor and more",
        "Agent-aware terminals with status and notifications",
        "One-click git worktrees",
        "Built-in browser and the cate CLI",
      ],
      cta: "Learn more about CATE",
      github: "GitHub",
    },
    aiPeek: {
      title: "Or let our AI answer.",
      lead: "We gave this page a context-aware AI layer, exactly the kind we build for you. Ask a question, and a matching interface opens live below.",
      hint: "↓ scroll to see the layer in action",
    },
  },
  footer: {
    impressum: "Imprint",
    datenschutz: "Privacy",
    rights: "All rights reserved",
  },
  about: {
    title: "About",
    subtitle:
      "We're 0-AI, a small studio in Stuttgart building AI-powered software with care, speed, and transparency.",
    stats: [
      { v: "2026", k: "Founded in Stuttgart" },
      { v: "1 to 2 wk.", k: "To first prototype" },
      { v: "100%", k: "Own devs, no subcontractors" },
    ],
    foundersEyebrow: "Founders",
    foundersTitle: "Two brothers, one studio",
    foundersBody:
      "0-AI was founded by Paul and Anton Horn, with the ambition to build AI not as a feature but as the central interaction layer. We combine design thinking with deep engineering and work directly with our clients, no account manager in between.",
    paul: {
      role: "CEO / Design & Frontend",
      name: "Paul Horn",
      body: "Studied online media management with a focus on design, user experience, communication, frontend and ideation. Owns concept, design system and the AI-native UI layer. Before that, several years of product and agency work.",
    },
    anton: {
      role: "CTO / Engineering",
      name: "Anton Horn",
      body: "Studied media informatics. Owns architecture, backend, infrastructure, and the LLM integration layer. Deep in Bun, TypeScript and self-hosted deploys. Everything we build runs on stacks you can keep running without us.",
    },
    coreBlocks: [
      {
        h: "Who we are",
        b: "0-AI is the product and advisory arm of 0-AI UG (haftungsbeschränkt), based in Stuttgart. We're a small team of engineers and designers, convinced that AI is fundamentally changing how people interact with software, and that most companies aren't keeping up.",
      },
      {
        h: "What we believe",
        b: "AI shouldn't feel like a feature bolted on after the fact. It should reshape the whole experience, from how users discover features to how they reach their goals. We design and build for that future.",
      },
      {
        h: "How we work",
        b: "We ship in weeks, not quarters. We build in the open. We use the best tools available, Bun, React, TypeScript, Claude, and aren't afraid to throw away code that doesn't serve the user. Every project starts with understanding the problem, not the technology.",
      },
      {
        h: "What sets us apart from agencies",
        b: "We don't sell slides, person-days, or interchangeable stacks. You talk directly to the people who also write the code. What we build is yours: repository, hosting, data. Even if you decided to drop us tomorrow.",
      },
    ],
    principlesEyebrow: "Principles",
    principlesTitle: "What we work by",
    principles: [
      { n: "01", title: "Weeks, not quarters", body: "We ship in short cycles. First usable prototype usually in 1 to 2 weeks, on a real stack, with real content, real AI." },
      { n: "02", title: "The same people design and build", body: "No handover ping-pong between agency, designer and dev studio. The person sketching the concept also writes the code." },
      { n: "03", title: "Code you can throw away", body: "We don't cling to anything that doesn't serve the user. Refactor is normal, not an emergency." },
      { n: "04", title: "Open by default", body: "Our stack is open. Our tools are open source. You get keys, repos, everything. No vendor lock-ins." },
    ],
    productsEyebrow: "Own products",
    productsTitle: "What we work with, and what we build ourselves",
    productsLead:
      "We build the tools we use every day, plus apps and services for everyone. From an IDE for coding agents and a deployment platform to an SEO audit service and two iOS apps.",
    products: [
      { slug: "cate", name: "CATE", desc: "Infinite canvas IDE for coding agents." },
      { slug: "one-click-deploy", name: "Open CLI Deployment", desc: "Open-source alternative to Heroku and Railway." },
      { slug: "sight", name: "Sight", desc: "SEO and AI visibility audits for your website." },
      { slug: "skyline", name: "Skyline Spotter", desc: "iOS app for discovering and collecting buildings." },
      { slug: "foody", name: "Foody", desc: "iOS app for pantry, shopping list and recipes." },
    ],
    githubCard: {
      title: "Everything on GitHub",
      body: "Our repositories are public. Read the code, fork it, file issues. Even our internal tools grow in the open.",
    },
    stackEyebrow: "Stack",
    stackTitle: "What we build with",
    stackLead:
      "A lean stack that starts fast, feels good, and runs anywhere. No frameworks we'll have to drop 18 months from now.",
    stack: [
      { label: "Bun", note: "Runtime & bundler" },
      { label: "React 19", note: "UI" },
      { label: "TypeScript", note: "Strict, end-to-end" },
      { label: "Tailwind v4", note: "Design system" },
      { label: "Claude / OpenRouter", note: "LLM layer" },
      { label: "MCP", note: "Tool use & agents" },
      { label: "Hetzner / Docker", note: "Self-hosted deploys" },
      { label: "SQLite / Postgres", note: "Data" },
    ],
    servicesEyebrow: "Services",
    servicesTitle: "What we offer",
    services: [
      { label: "Consulting", href: "/consulting", desc: "AI strategy, architecture reviews and technical advisory for teams building with LLMs." },
      { label: "Design & Build", href: "/design", desc: "AI-native UI/UX design and full-stack engineering, from concept to production." },
    ],
    ctaTitle: "Have an idea, or a website that should feel new again?",
    ctaLead: "The intro call is free. We'll tell you honestly whether we're the right fit.",
  },
  contact: {
    title: "Contact",
    subtitle: "Let's talk about your project. We respond within 24 hours.",
    writeUsHeading: "Write to us",
    writeUsBody:
      "Whether you're exploring AI for the first time or scaling an existing system, we'd love to hear from you. We work with companies of all sizes, from startups shipping their first AI feature to enterprises modernising legacy systems.",
    channelsHeading: "Ways to reach us",
    email: "Email",
    github: "GitHub",
    location: "Location",
    locationValue: "Stuttgart, Germany. Remote-first",
    foundersHeading: "Your contacts",
    founders: [
      {
        name: "Paul Horn",
        role: "CEO / Design & Frontend",
        bio: "Studied online media management with a focus on UX, communication and ideation. Owns concept, design system, project management and the AI-native UI layer.",
        image: "/assets/team/anton-horn.png",
        email: "paul@cero-ai.com",
        linkedin: "https://www.linkedin.com/in/paulwphorn/",
      },
      {
        name: "Anton Horn",
        role: "CTO / Engineering",
        bio: "Studied media informatics. Owns architecture, AI integration, backend and infrastructure. Deep in Bun, TypeScript, LLM stacks and self-hosted deploys.",
        image: "/assets/team/paul-horn.png",
        email: "anton@cero-ai.com",
        linkedin: "https://www.linkedin.com/in/anton-horn/",
      },
    ],
    expectHeading: "What to expect",
    steps: [
      { step: "01", label: "Intro call", desc: "60 minutes. We get to know your project, your goals and your constraints." },
      { step: "02", label: "Proposal", desc: "Within 48 hours. Scope, timeline and price tailored to your needs." },
      { step: "03", label: "Kickoff", desc: "We start building. You see progress in days, not weeks." },
    ],
  },
  pricing: {
    title: "Pricing",
    subtitle:
      "Three packages for every size. Maintenance and AI layer optionally combinable. Prices one-off, plus VAT.",
    wartungLabel: "Maintenance",
    wartungOhne: "None",
    kiLabel: "AI layer",
    wartungEyebrow: "Maintenance contract",
    wartungLead:
      "12-month term. The fixed website price is reduced accordingly. The difference funds ongoing care.",
    filterOhneEyebrow: "No maintenance",
    filterOhneTitle: "Website only",
    filterOhneDesc: "Fixed price, no subscription.",
    selected: "✓ selected",
    select: "Select →",
    popular: "Popular",
    recommended: "Recommended",
    onRequest: "On request",
    onRequestNote: "individual quote",
    onceTax: "one-off, plus VAT.",
    maintenance: "+ €{{price}}/mo maintenance",
    kiAdd: "+ €{{price}}/mo AI layer ({{plan}})",
    bookCall: "Book a call",
    requestProject: "Request a project",
    kiSectionTitle: "AI layer on your website.",
    kiSectionLead:
      "Context-aware AI layer, trained on your content. Basic with 1,000 answers a month or Pro with 4,000, or pay per answer. Prices include VAT.",
    kiMonthSuffix: "/month, 12-month term",
    kiActive: "✓ active",
    kiAddBtn: "Add →",
    footerNote:
      "Website prices plus VAT, AI layer prices include VAT. Maintenance contracts have a 12-month minimum term. The AI layer runs on a 1, 6 or 12-month term, or pay per use at €0.02 per answer. Setup is free if you do it yourself, €99 together, €249 done for you. Not sure which package fits?",
    footerNoteLink: "Talk to us.",
  },
  products: {
    title: "Products & offerings",
    subtitle:
      "Three core offerings for your website, plus the open-source tools and products we build and operate alongside them.",
    coreHeading: "Core offerings",
    coreLead: "What we do for your website. Direct collaboration with the people who also write the code.",
    ownHeading: "Own products",
    ownLead:
      "Open-source tools and platforms we build in parallel. They form the foundation for our client work.",
    status: {
      openSource: "Open source",
      beta: "Beta",
      inDev: "In development",
      webApp: "Web app",
      ios: "iOS app",
    },
    list: [
      {
        slug: "cate",
        name: "CATE",
        tagline: "Infinite canvas IDE for coding agents",
        description: "Open-source IDE on a zoomable canvas for running parallel coding agents. Agent-aware terminals, git worktrees, editor and browser panels.",
        status: "openSource",
      },
      {
        slug: "one-click-deploy",
        name: "Open CLI Deployment",
        tagline: "Deploy containers to your own servers.",
        description: "Self-hosted, CLI-first platform that builds images from Git and deploys them to your own servers, with TLS, rollbacks and scaling. The alternative to Heroku, Railway and Render.",
        status: "openSource",
      },
      {
        slug: "sight",
        name: "Sight",
        tagline: "SEO and AI visibility audits",
        description: "Scan your site once and see how it performs in Google and in AI answers: where ChatGPT, Gemini, Claude and Perplexity mention you, what blocks you, and what to fix first.",
        status: "webApp",
      },
      {
        slug: "skyline",
        name: "Skyline Spotter",
        tagline: "Look up and explore.",
        description: "iOS app for discovering the buildings around you. Browse cities, collect sightings with your own photos and suggest missing buildings and facts.",
        status: "ios",
      },
      {
        slug: "foody",
        name: "Foody",
        tagline: "Make more of what you have.",
        description: "iOS app that knows your pantry, keeps the shopping list current and finds recipes that actually fit. One shared household state.",
        status: "ios",
      },
    ],
  },
  productPage: {
    websiteLinkAria: "Open website",
    galleryLabel: "Screenshots",
    downloadTitle: "Download now.",
    downloadLead: "Available for macOS, Windows and Linux. Open source, MIT-licensed.",
    downloadBtn: "Download",
  },
  productDetails: {
    "ai-overlay": {
      title: "AI Overlay",
      subtitle: "A context-aware AI layer on your existing website, trained on your content. Answers questions, generates fitting interfaces live, guides visitors to your content. No re-launch.",
      sections: [
        { heading: "What is the AI Overlay?", body: "A lightweight layer that sits on top of your existing website. Visitors ask a question in natural language. The layer answers in context based on your content and renders a matching interface live, for example cards, tables, forms, comparisons. Exactly like the layer on this page." },
        { heading: "No re-launch needed", body: "The overlay is embedded via a script tag, comparable to an analytics snippet. Your existing site stays untouched. Content, routing, CMS and SEO remain unchanged. Integration in 1 to 2 weeks, including a first training crawl." },
        { heading: "Trained on your content", body: "We index your website, documents and optionally further sources like Notion, Confluence, helpdesk, PDFs. Answers are grounded, with source citations instead of hallucinations. Content is re-indexed on changes, manually or via webhook." },
        { heading: "Generative interfaces", body: "Instead of plain text the layer renders live components: contact cards, project galleries, price comparisons, FAQ lists, forms. The choice is contextual. The layer decides per question which interface fits best." },
        { heading: "Pricing & operations", body: "Basic (1,000 answers a month) costs €49/month month to month, €39 on a 6-month term and €29 on a 12-month term. Pro (4,000 answers) costs €129, €99 and €79. Prefer no base fee? Pay per use is €0.02 per answer. Extra packs of 1,000 answers cost €20. Setup is free if you do it yourself, €99 together and €249 if we do it for you. Optional care from €29/month. All prices include VAT." },
        { heading: "When does it make sense?", body: "Companies with explanation-heavy products, extensive documentation, lots of support requests, or complex catalogues. Less useful on pure one-pagers or shops with standard search." },
      ],
    },
    "website-rework": {
      title: "Website Rework, AI First",
      subtitle: "We redesign your website with AI as the central interaction, not as a bolted-on chatbot. Conversion-relevant paths, generative content, clear architecture. Fixed price, fixed phases.",
      sections: [
        { heading: "What does AI First mean?", body: "AI isn't a widget in the corner, it's part of the main interaction. Navigation, search, explanation, configuration, advisory. All of it can become conversational or generative where it makes sense. Classic page structure stays in place for SEO and direct entry." },
        { heading: "What you get", body: "New information architecture, design system, AI-native components, engineering including hosting setup. Frontend in React/TypeScript, backend in Bun/Node, AI through Anthropic/OpenAI/OpenRouter. Fully open-source stack on request." },
        { heading: "Phases & fixed price", body: "Phase 1: discovery & concept (fixed price, 1 to 2 weeks). Phase 2: prototype on a real stack (fixed price, 1 to 2 weeks). Phase 3: full build in 2-week iterations. Before every phase you know what it costs and what you'll get." },
        { heading: "Live prototype after 1 to 2 weeks", body: "Before the full budget is committed you see a working prototype with real content and real AI. No mock-up, no slides. You click through and decide whether the direction is right." },
        { heading: "Design and engineering from one team", body: "No handovers between agency, designer and dev studio. The same people conceive, design and build. That saves time, avoids friction and leads to solutions that actually work in implementation." },
        { heading: "Long-term support", body: "On request we take over maintenance, continued development and operations with the same devs who built it. Monthly iteration, clear roadmap, predictable effort. Details on the advisory page." },
      ],
    },
    cate: {
      title: "CATE",
      subtitle: "An open-source infinite canvas IDE for parallel coding agents. Terminals, editors and browsers float on a zoomable canvas, and every terminal shows whether its agent is working, finished or waiting.",
      tagline: "One infinite canvas for all your coding agents.",
      highlights: [
        "Agent chat panel with Codex, Claude Code, Cursor, Grok and more",
        "Agent-aware terminals with status and notifications",
        "One-click git worktrees, one territory per branch",
        "Monaco editor and xterm.js terminals with WebGL",
        "Built-in browser panel and the cate CLI for agents",
        "Local and remote runtimes via end-to-end encrypted Cate Connect",
        "Available for macOS, Windows and Linux",
        "Open source under the MIT licence",
      ],
      sections: [
        { heading: "What is CATE?", body: "CATE rethinks the IDE as an infinite canvas for working with parallel coding agents. Instead of tabs and split views you arrange terminals, editors and browser previews spatially, like Figma but for code. A workspace is simply an opened folder, with no config files." },
        { heading: "Who is it for?", body: "Developers who run several coding agents at once and need to see which ones are working, finished or waiting for input. If you regularly juggle agents, terminals, editors, logs and previews, CATE gives you a spatial layout that tab-based IDEs can't." },
        { heading: "Agents first", body: "An agent chat panel integrates Codex, Claude Code, Cursor, Grok, OpenCode and more. Agent-aware terminals report running, waiting and finished states, send notifications and resume sessions with scrollback after a restart. Agents running in a Cate terminal can drive the built-in browser, read other terminals and open files through the cate CLI." },
        { heading: "Worktrees & spatial workflows", body: "Create a git worktree and branch from a local branch, a remote branch or a pull request in one click. Each worktree gets its own coloured territory on the canvas. Panels can float, dock into tabs and splits or detach into their own window, and the layout persists per project." },
        { heading: "Editor, git & remote", body: "Monaco editor, xterm.js terminals with WebGL, multi-repo source control with side-by-side diffs, ripgrep search, a Cmd+K command palette, rebindable shortcuts and data-driven themes. Workspaces can run in local or remote runtimes and keep running when the window closes. Devices pair via QR code over an end-to-end encrypted Cate Connect link." },
        { heading: "Tech stack & open source", body: "Electron 41, React 18, TypeScript, Tailwind CSS, Zustand, Monaco Editor and xterm.js with node-pty. MIT-licensed and free on GitHub, with builds for macOS (also via Homebrew), Windows and Linux." },
      ],
    },
    "one-click-deploy": {
      title: "Open CLI Deployment",
      subtitle: "A self-hosted, CLI-first platform for deploying containers to your own servers. The open-source alternative to Heroku, Railway and Render.",
      sections: [
        { heading: "What is Open CLI Deployment?", body: "A CLI-first platform that builds immutable OCI images from Git and rolls them out onto your own Linux servers. You work through the ocd CLI; a small self-hosted panel holds desired state, credentials, history and recovery. Guided setup with ocd bootstrap, optional provisioning of Hetzner servers and volumes." },
        { heading: "Core features", body: "Deployments from any Git repository via .ocd-deploy.json manifests, including multi-service stacks with inferred start order. Digest-pinned immutable releases with promote and rollback. Logs, shell, file copy, pause and scale from the CLI. Incident and recovery flows, notifications and an installable agent skill." },
        { heading: "Security & operations", body: "Traefik handles ingress with automatic TLS. The panel is protected with passkeys (WebAuthn). Builds run on BuildKit workers, with Postgres and S3-compatible storage behind the panel. GitHub webhooks can trigger builds." },
        { heading: "Open source", body: "Fully open source under the MIT licence. Built with Bun, TypeScript and React 19. The CLI runs on macOS and Linux (x64 and arm64), the panel on any Linux Docker host. Available on GitHub." },
      ],
    },
  },
  services: {
    consulting: {
      title: "Consulting",
      subtitle: "AI strategy, architecture reviews and technical advisory for teams building with LLMs and modern AI infrastructure.",
      sections: [
        { heading: "What we do", body: "We help companies find their footing in the AI landscape, from evaluating suitable models to designing scalable production architectures. We've shipped AI-powered products across industries and bring that experience into your team." },
        { heading: "Strategy & roadmapping", body: "AI adoption is more than picking a model. We help you identify high-impact opportunities, evaluate build-vs-buy decisions and create pragmatic roadmaps that deliver in weeks, not quarters." },
        { heading: "Architecture reviews", body: "Already building? We review your AI stack, RAG pipelines, agent architectures, prompt engineering, model selection, cost optimisation, and point out where performance or reliability are leaving room on the table." },
        { heading: "Team enablement", body: "We work directly with your developers and transfer knowledge as we build. The goal is always to make your team more capable, not to create dependency." },
      ],
    },
    design: {
      title: "Design",
      subtitle: "UI/UX for AI-native products. We design interfaces where AI feels natural, not bolted on.",
      sections: [
        { heading: "AI-native design", body: "AI interfaces are more than chatbots. We design experiences where AI enriches every interaction, from smart defaults to contextual suggestions to generative content, all as a natural part of the product." },
        { heading: "From concept to system", body: "We start with user research and rapid prototypes, then develop full design systems with component libraries, interaction patterns and documentation your team can extend." },
        { heading: "What we've designed", body: "Spatial IDEs, conversational widgets, narrative platforms, design editors, trading dashboards. Every project extends the boundaries of what AI interfaces can feel like." },
        { heading: "Tools & approach", body: "Figma for design, React for prototypes, Tailwind for systems. We iterate fast. You see working interfaces in days, not weeks. Every design decision is grounded in real technical constraints." },
      ],
    },
    engineering: {
      title: "Engineering",
      subtitle: "Full-stack AI development. From prototype to production in weeks, not quarters.",
      sections: [
        { heading: "How we build", body: "We ship fast without compromising on quality. Our stack, Bun, React, TypeScript, Claude/Anthropic SDK, is built for speed and reliability. We bet on modern tools because they let us move faster and produce better results." },
        { heading: "AI integration", body: "RAG pipelines, agent architectures, tool-calling systems, streaming UIs, MCP servers. We've built AI production systems that handle real traffic and real users. We know the pitfalls." },
        { heading: "Full stack", body: "Frontend (React, Next.js, Tailwind), backend (Bun, Node.js, Go, Python), databases (MongoDB, Qdrant, SQLite, Redis), infrastructure (Docker, Terraform, Hetzner, Vercel). We own the whole stack so nothing falls through the cracks." },
        { heading: "Open source first", body: "We build in public wherever we can. Our tools, libraries and patterns are MIT/CC-licensed. Contributing to the ecosystem isn't a side project, it's how we work." },
      ],
    },
  },
  offering: {
    forWhomLabel: "Who it's for",
    notForLabel: "And who it's not for",
    deliverablesTitle: "What you actually get.",
    deliverablesLead: "Scope that is clearly bounded. No vagueness, no „we'll see“.",
    processTitle: "How the collaboration runs.",
    processLead: "Clear phases, clear milestones. On day one you know what happens when.",
    pricingTitle: "What it costs.",
    faqTitle: "Frequent questions.",
    ctaTitle: "Let's talk concretely about your project.",
    ctaLead:
      "Free 60-minute intro call. In an hour we figure out whether {{offering}} is the right lever and what a realistic next step would be.",
    bookCall: "Book intro call",
    fullPricing: "Full pricing",
    relatedLabel: "Related",
    "ai-overlay": {
      title: "AI Overlay",
      subtitle:
        "A context-aware AI layer on your existing website, trained on your content, no re-launch, integrated in 1 to 2 weeks.",
      intro:
        "Your website stays as it is. We add a thin AI layer on top that knows the content of your site, answers your visitors' questions in natural language, and generates matching interfaces live: product comparisons, contact forms, calculators, roadmaps. The same pattern you can try out on this page yourself.",
      forWhom: [
        "You have a website that works, but visitors can't find information.",
        "You want to test AI without risking a re-launch.",
        "You have documentation, product data or knowledge articles that hardly anyone reads.",
        "You want to measure what your visitors actually ask for.",
      ],
      notForWhom: [
        "You want a simple FAQ bot with no link to your content.",
        "You need unlimited answers with no usage cap.",
        "You're looking for white-label SaaS. We build for you, not for many.",
      ],
      deliverables: [
        { title: "Embed script & overlay UI", body: "A single <script> tag that activates the layer on every subpage. The overlay opens on demand, no annoying chat bubble that pops up from the start." },
        { title: "Knowledge index", body: "We crawl your website, normalise content and build a vector index. Optionally PDFs, Notion, Confluence or your own sources on top." },
        { title: "Generative interfaces", body: "Instead of returning plain text the layer renders fitting components live: product cards, contact form, price calculator, comparison table, appointment booking. We define the components together with you." },
        { title: "Analytics dashboard", body: "What do visitors really ask? Which answers end in a conversion? We deliver a simple dashboard with top questions, knowledge gaps and conversion paths." },
        { title: "Model selection & routing", body: "Small model for trivial requests, large for complex. We route automatically and keep token costs under control. Model provider (Anthropic, OpenAI, local) is configurable." },
        { title: "Maintenance optional", body: "Re-index on content changes, model updates, prompt tuning. Flat-rate monthly or per sprint. No vendor lock-in. You can operate the layer yourself at any time." },
      ],
      process: [
        { label: "Day 1 to 2", title: "Kickoff & content audit", body: "We review your content, define the first three generative components and set success metrics." },
        { label: "Day 3 to 7", title: "Index & prompt engineering", body: "We crawl, index, write system prompts and build the components. You get a live prototype to click through yourself." },
        { label: "Day 8 to 10", title: "Iteration with real content", body: "We go through answers together: where the layer hallucinates, where it's too cautious, where sources are missing. From that follows the final polish." },
        { label: "Day 11 to 14", title: "Embed & go-live", body: "Script on your site, analytics live, model routing in production. Hand-off documentation so your team can operate the layer without us." },
      ],
      faq: [
        { q: "What does it cost ongoing?", a: "Basic (1,000 answers a month) is €29 to €49/month depending on term, Pro (4,000 answers) €79 to €129/month. Extra packs of 1,000 answers are €20. Prices include VAT. Your plan caps usage, so there are no surprise bills." },
        { q: "What happens to user data?", a: "By default no storage of requests beyond the analytics minimum. Hosting in Germany possible. GDPR-compliant, with DPA." },
        { q: "Does this work on any website?", a: "Yes. The embed is framework-agnostic. Static site, WordPress, Shopify, Webflow, custom React. We just need a place where a script can be embedded." },
        { q: "What if the AI says something wrong?", a: "We answer exclusively from your sources, with citation and a link to the original. Where sources are missing, the layer says so and offers a contact path. We measure hallucination rate continuously." },
      ],
      pricing: {
        headline: "From €29/month",
        lines: [
          "Basic: 1,000 answers/month for €49 month to month, €39 on 6 months, €29 on 12 months.",
          "Pro: 4,000 answers/month for €129, €99 or €79. Pay per use: €0.02 per answer, no base fee.",
          "Setup free if you do it yourself, €99 together, €249 done for you. Optional care from €29/month.",
        ],
        note: "All prices include VAT. Extra 1,000-answer packs cost €20.",
      },
      related: [
        { label: "Website Rework, AI First", href: "/services/website-rework" },
        { label: "Long-term Partnership", href: "/services/langzeit-betreuung" },
        { label: "Try AI on this page", href: "#ai" },
      ],
    },
    "website-rework": {
      title: "Website Rework, AI First",
      subtitle:
        "Rethink existing sites as AI-native. We redesign your website with AI as the central interaction. Fixed price, fixed phases, predictable launch.",
      intro:
        "A chatbot in the top right isn't an AI-native product. An AI-first website rethinks conversion paths: what still needs to be a page, what becomes a conversation, what is generated live from data? We do this with you, not as workshop theatre but as a deliverable redesign with a clear architecture.",
      forWhom: [
        "Your current site feels like 2018, and you know the problem is structural.",
        "You have complex products, configurators or advisory-heavy offers.",
        "Your team can continue the new stack themselves but doesn't want to start from zero.",
        "You're not looking for a marketing refresh, but a real product decision.",
      ],
      notForWhom: [
        "You primarily want a visual refresh. A pure design agency is cheaper for that.",
        "You have no productive backend or data foundation for AI to work with.",
        "You need 50+ templates at once. We're not the right scale.",
      ],
      deliverables: [
        { title: "Architecture concept", body: "Which pages stay pages, which become conversation flows, which are rendered live from data. Including sitemap, routing plan and data flow diagram." },
        { title: "Design system (code-first)", body: "Components, typography, colours, motion. All directly as React + Tailwind. No Figma graveyard that never ships. A design system is code you can keep building on after us." },
        { title: "Live prototype after 1 to 2 weeks", body: "Working, deployable prototype of the most important flows, with real AI layer, real data, real routing. So you can decide early whether the direction is right." },
        { title: "Conversion-relevant AI paths", body: "We identify 3 to 5 spots where AI isn't just novelty but actually drives conversion: product advisory, configurator, onboarding, lead qualification, self-service." },
        { title: "Generative content", body: "Where it makes sense, we generate content live from sources instead of maintaining it editorially: personalised case studies, dynamic price calculators, language variants." },
        { title: "Deployment & handover", body: "We deploy on your infrastructure, or ours if preferred (Hetzner, Docker, optionally our own Open CLI Deployment). Full documentation, tech handover to your team." },
      ],
      process: [
        { label: "Phase 1", title: "Discovery & concept (week 1 to 2)", body: "Workshops with your team, technical audit, architecture draft. Result: documented concept, fixed price for the following phases, go/no-go decision." },
        { label: "Phase 2", title: "Prototype (week 3 to 4)", body: "Deployable prototype of the three most important flows. By end of phase 2 it's clear whether the redesign delivers on its promise, before the full budget is committed." },
        { label: "Phase 3", title: "Build (week 5 to 10)", body: "Incremental delivery in 2-week sprints. Frontend, backend, AI layer, content migration, SEO preservation. You can follow along on staging weekly." },
        { label: "Phase 4", title: "Launch & stabilisation (week 11 to 12)", body: "Cut-over, monitoring, performance tuning, bugfix sprint. Optionally followed by long-term partnership." },
      ],
      faq: [
        { q: "How long does it take in total?", a: "Typically 10 to 12 weeks for mid-sized sites. Very complex projects longer. We clarify that in phase 1 before the build commit." },
        { q: "What happens to our SEO?", a: "301 redirect map, structured data, clean server rendering. AI flows as an additional layer, not a replacement for indexable content. SEO is mandatory, not optional." },
        { q: "Can we exit after phase 2?", a: "Yes. The first two phases are deliberately deliverable on their own. Concept and prototype are usable results even if you don't continue." },
        { q: "What tech stack options do we have?", a: "Default: Bun + React + Tailwind + Anthropic SDK. Alternatively Next.js, Astro, or integration into existing stacks (Rails, Django, Laravel). Decision after phase 1." },
      ],
      pricing: {
        headline: "Fixed price per phase",
        lines: [
          "Phase 1 (concept): flat fixed price after the intro call.",
          "Phase 2 (prototype): fixed price after the concept. You know the number before we start.",
          "Phase 3 + 4 (build & launch): fixed price after the prototype, based on concrete scope.",
        ],
        note: "You commit only to the next phase at a time. No open-end contracts. Concrete amounts in the intro call.",
      },
      related: [
        { label: "AI Overlay", href: "/services/ai-overlay" },
        { label: "Long-term Partnership", href: "/services/langzeit-betreuung" },
        { label: "Pricing overview", href: "/pricing" },
      ],
    },
    "langzeit-betreuung": {
      title: "Long-term Partnership",
      subtitle:
        "Advisory, maintenance and continued development with the same people who built it. You talk to devs, not account managers.",
      intro:
        "A website isn't done when it goes online. That's where it starts. AI models change every month, content grows, conversion hypotheses want testing. We stay on board as a small, well-rehearsed team with a direct line, monthly predictable, no service layer in between.",
      forWhom: [
        "You have an AI-powered site (from us or elsewhere) and want to keep developing it.",
        "You don't want to gather a new quote for every small step.",
        "You value continuity. The same devs who know your codebase.",
        "You want monthly iteration rather than big re-launches.",
      ],
      notForWhom: [
        "You need 24/7 incident response. For that we recommend specialised providers.",
        "You're looking for a classic maintenance package with pure bugfix SLA.",
        "You want to buy hours instead of outcomes. We work sprint-based, not hourly.",
      ],
      deliverables: [
        { title: "Monthly sprint budget", body: "Fixed hours per month, plannable across 6 to 12 months. Unused hours roll forward one month. No surprises on the invoice." },
        { title: "Direct Slack/Discord channel", body: "A shared channel with the devs. Response within 4 hours during office hours. No tickets, no first-level layer." },
        { title: "Roadmap upkeep", body: "We keep a visible roadmap with you: quarterly themes, monthly sprint goals, clear backlog. You always know where we stand." },
        { title: "Model & stack updates", body: "New Claude or GPT model? We test, evaluate, migrate, without you having to organise it. Stack updates (Bun, React, Tailwind) run quietly along." },
        { title: "AI advisory included", body: "Strategic advisory is part of the retainer, not an extra. If you're considering using AI in a new spot, we think along, before anyone builds." },
        { title: "Quarterly review", body: "Every three months a structured review: what was delivered, what worked, what we change in approach. Written, with metrics." },
      ],
      process: [
        { label: "Month 1", title: "Onboarding (if new)", body: "Code audit, close documentation gaps, monitoring sharp, backup strategy. We want to know your codebase before we keep building on it." },
        { label: "Every month", title: "2-week sprints", body: "Planning at sprint start, demo at sprint end. Every two weeks you see an increment live on staging and decide what to prioritise next." },
        { label: "Ongoing", title: "Async in the channel", body: "Questions, ideas, smaller adjustments run through the shared channel. Larger topics move into the backlog for the next sprint." },
        { label: "Quarterly", title: "Review & re-scoping", body: "Every three months we check whether the sprint budget still fits: up, down, or pause. No automatic extension, no hidden auto-renew." },
      ],
      faq: [
        { q: "What does it cost?", a: "Monthly retainer with a fixed hour budget, plannable across 6 to 12 months. We stagger by desired capacity. We share the concrete amount after a short scope conversation so it matches your real need. Fixed price, 3-month minimum term, monthly cancellable thereafter." },
        { q: "What if we don't need anything in a given month?", a: "Hours roll forward one month. If you don't need anything for longer, we pause the retainer for free, reactivable any time." },
        { q: "Do we have to have built with you?", a: "No. We also take over external codebases, typically after a 2 to 4-week onboarding offered at a reduced fixed price." },
        { q: "What sets this apart from a normal agency?", a: "You talk directly to the devs who write the code. No project manager translating between you and the team. No ticket system with SLA theatre. Small team, high continuity." },
      ],
      pricing: {
        headline: "Monthly retainer, fixed price by scope",
        lines: [
          "Three tiers: channel support & roadmap upkeep / monthly sprints / dedicated build-partner capacity.",
          "Fixed hours per month, unused hours roll forward one month.",
          "Concrete amount after a short scope conversation, no standard-package pressure.",
        ],
        note: "3-month minimum term, monthly cancellable thereafter. No auto-renewal traps.",
      },
      related: [
        { label: "AI Overlay", href: "/services/ai-overlay" },
        { label: "Website Rework, AI First", href: "/services/website-rework" },
        { label: "Get in touch", href: "/contact" },
      ],
    },
  },
  blog: {
    title: "Blog",
    subtitle: "Thoughts on AI engineering, design patterns and insights from building and shipping AI-powered products.",
    footerNoteBefore: "More articles coming soon. Want to stay in the loop? Write to us at",
    readingSuffix: "min read",
    posts: [
      {
        title: "Why we chose Bun over Node for production AI apps",
        date: "2025-03-15",
        reading: 6,
        summary: "Bun's integrated bundler, SQLite driver and faster startup times made it the right choice for our AI-heavy server workloads. What we learned after 6 months in production.",
        tags: ["Bun", "Performance", "Architecture"],
      },
      {
        title: "Building RAG pipelines that actually work",
        date: "2025-02-28",
        reading: 8,
        summary: "Most RAG tutorials end at „embed your documents and query.“ Real production RAG needs chunking strategies, hybrid search, re-ranking and careful evaluation. A practical guide from real-user deployment.",
        tags: ["RAG", "AI", "LangChain"],
      },
      {
        title: "MCP: the protocol changing how AI uses tools",
        date: "2025-02-10",
        reading: 5,
        summary: "The Model Context Protocol lets AI models interact with external tools through a standardised interface. Here's why every AI-powered app should consider MCP.",
        tags: ["MCP", "Tools", "Architecture"],
      },
      {
        title: "Designing AI interfaces that don't feel like chatbots",
        date: "2025-01-22",
        reading: 7,
        summary: "Chat is the lazy default for AI UX. Real AI-native design means smart defaults, contextual suggestions and generative content woven into the product. Not a sidebar chatbot.",
        tags: ["Design", "UX", "AI"],
      },
    ],
  },
  careers: {
    title: "Careers",
    subtitle: "We're a small team building AI-powered software. If you're excited about shipping real products with modern tools, we'd love to hear from you.",
    howHeading: "How we work",
    howBody: "We're a remote-first studio based in Stuttgart. We build products, deliver advisory projects and contribute to open source, often all in the same week. We value people who are curious, move fast and care about quality.",
    valuesHeading: "Values",
    values: [
      { label: "Remote-first", desc: "Work from anywhere. We're based in Germany, but our work isn't tied to a location." },
      { label: "Ship fast", desc: "We measure progress in days, not sprints. If it works, ship. If not, learn and keep going." },
      { label: "Build in the open", desc: "Our projects are open source. We share what we learn because it makes everyone better." },
      { label: "Quality counts", desc: "Good code, good design, good communication. We care about the details users notice." },
    ],
    rolesHeading: "Roles",
    requirementsLabel: "What we're looking for",
    roles: [
      {
        title: "Full-stack AI developer",
        type: "Freelance / contract",
        description: "Build AI-powered products end to end. You work across the whole stack: React frontends, Bun/Node backends, LLM integrations, vector databases and deployment infrastructure.",
        requirements: [
          "Solid experience with TypeScript and React",
          "Familiarity with LLMs, RAG and AI tooling",
          "Comfortable deploying to production on your own",
          "Experience with Bun, Next.js or similar modern runtimes",
        ],
      },
      {
        title: "AI/UX designer",
        type: "Freelance / contract",
        description: "Design interfaces for AI-native products. No chatbots, real product experiences with AI woven into every interaction. You take work from concept to high-fidelity prototype.",
        requirements: [
          "Portfolio with AI or complex product design",
          "Confident with Figma and design systems",
          "Ability to prototype in code (React/Tailwind) a plus",
          "Understanding of LLM capabilities and limits",
        ],
      },
    ],
    ctaHeading: "Your role not listed?",
    ctaBodyBefore: "We're always interested in meeting talented people. Write to us at",
    ctaBodyAfter: "with a few lines about yourself and a link to your work.",
  },
  work: {
    title: "Projects",
    subtitle: "The products we've designed, built and shipped.",
    cases: [
      {
        title: "CATE",
        description: "Infinite canvas IDE for parallel coding agents. Agent-aware terminals, git worktrees, editor and browser panels on one zoomable canvas. Open source under the MIT licence.",
      },
      {
        title: "Open CLI Deployment",
        description: "Self-hosted, CLI-first platform for deploying containers to your own servers, with TLS, rollbacks and scaling. The alternative to Heroku, Railway and Render.",
      },
      {
        title: "Sight",
        description: "SEO and AI visibility audits. Scan a site and get a scored report covering classic SEO and how ChatGPT, Gemini, Claude and Perplexity see you.",
      },
      {
        title: "Skyline Spotter",
        description: "iOS app for discovering buildings around you. Browse cities, collect sightings and contribute missing buildings and facts.",
      },
      {
        title: "Foody",
        description: "iOS app for pantry, shopping list and matching recipes in one shared household state.",
      },
    ],
  },
  stack: {
    title: "Tech Stack",
    subtitle: "The tools, frameworks and infrastructure we use to build and ship AI-powered products.",
    categories: [
      {
        name: "Frontend",
        tools: [
          { name: "React", desc: "UI framework across all our products" },
          { name: "Next.js", desc: "Full-stack React for production apps" },
          { name: "Tailwind CSS", desc: "Utility-first styling on every project" },
          { name: "TypeScript", desc: "Strict typing everywhere, no exceptions" },
          { name: "Monaco Editor", desc: "VS Code editor component for CATE" },
        ],
      },
      {
        name: "Backend & runtime",
        tools: [
          { name: "Bun", desc: "Fast JS runtime: server, bundler, test runner" },
          { name: "Node.js", desc: "Production runtime for established services" },
          { name: "FastAPI", desc: "Python backend for ML-heavy workloads" },
          { name: "Go", desc: "High-performance services and CLI tools" },
        ],
      },
      {
        name: "AI & LLMs",
        tools: [
          { name: "Claude / Anthropic SDK", desc: "Primary LLM for generation and reasoning" },
          { name: "OpenRouter", desc: "Multi-model routing and fallback" },
          { name: "LangChain", desc: "RAG pipelines and agent orchestration" },
          { name: "MCP", desc: "Model Context Protocol for tool-calling systems" },
          { name: "Vercel AI SDK", desc: "Streaming UI responses" },
        ],
      },
      {
        name: "Databases",
        tools: [
          { name: "MongoDB", desc: "Document store for flexible schemas" },
          { name: "Qdrant", desc: "Vector database for semantic search" },
          { name: "SQLite", desc: "Embedded database via bun:sqlite" },
          { name: "Redis", desc: "Caching, rate limiting, sessions" },
        ],
      },
      {
        name: "Infrastructure",
        tools: [
          { name: "Docker", desc: "Containerised deployments everywhere" },
          { name: "Terraform", desc: "Infrastructure as code for cloud resources" },
          { name: "Hetzner", desc: "European cloud hosting, fast and affordable" },
          { name: "Vercel", desc: "Frontend deploys and edge functions" },
          { name: "GitHub Actions", desc: "CI/CD for automated tests and deploys" },
        ],
      },
      {
        name: "Design & dev tools",
        tools: [
          { name: "Figma", desc: "Design and prototyping" },
          { name: "Electron", desc: "Desktop apps (CATE)" },
          { name: "xterm.js", desc: "Terminal emulation with WebGL rendering" },
          { name: "Zustand", desc: "Lightweight state management" },
        ],
      },
    ],
  },
  opensource: {
    title: "Open Source",
    subtitle: "We build in the open. Our tools, libraries and experiments are available to everyone. To use, to fork, to contribute to.",
    philosophyHeading: "Philosophy",
    philosophyBody: "We believe the best developer tools should be transparent and community-driven. When we build something useful, we share it. When we learn something hard, we document it. Open source isn't a marketing strategy. It's how we think software should be built.",
    contributeHeading: "Contribute",
    contributeBody: "We welcome contributions of any kind: bug reports, feature requests, documentation improvements and code. Contribution guidelines live in each repository's README.",
    viewAllRepos: "View all repositories",
    projectsHeading: "Projects",
    repos: [
      { name: "CATE", slug: "cate", description: "Infinite canvas IDE, a spatial development environment with Monaco editor, xterm.js terminal and browser panels on a zoomable canvas." },
      { name: "Open CLI Deployment", slug: "one-click-deploy", description: "Self-hosted, CLI-first platform for deploying containers to your own servers, with TLS, rollbacks and scaling." },
      { name: "0-AI", slug: null, description: "This website, a Bun + React fullstack app with an interactive AI-powered canvas, built with Tailwind CSS v4." },
    ],
  },
  notFound: {
    error: "Error 404",
    titles: [
      "This page is lost in\nlatent space.",
      "The model hallucinated\nthis URL.",
      "Token not found in the\ncontext window.",
      "Trained on zero\nweights.",
      "Embedded too deeply,\nno longer reachable.",
      "Prompt injection\nfailed.",
      "Segfault in the\nneural net.",
    ],
    stayNote: "Or stick around and pick up some points.",
    startHint: "PRESS SPACE OR TAP TO START",
    gameOver: "GAME OVER",
    restart: "SPACE / CLICK TO RESTART",
    runnerLabel: "Runner",
    controls: "SPACE · ↑ · CLICK · TAP",
  },
  impressum: {
    title: "Imprint",
    sec1Heading: "Information per § 5 DDG",
    sec1Body: "0-AI UG (haftungsbeschränkt)\nNeckartalstraße 127\n70376 Stuttgart\nGermany",
    sec2Heading: "Represented by",
    sec2Body: "Managing directors:\nPaul Willi Patrick Horn\nAnton Leo Rudolf Horn",
    sec3Heading: "Contact",
    sec3Body: "Email: contact@cero-ai.com",
    sec4Heading: "Register entry",
    sec4Body: "Commercial register entry\nRegister court: Amtsgericht Stuttgart\nRegister number: pending",
    sec5Heading: "VAT ID",
    sec5Body: "VAT identification number per § 27a UStG:\napplied for",
    sec6Heading: "Responsible for content per § 18 (2) MStV",
    sec6Body: "Paul Willi Patrick Horn\nNeckartalstraße 127\n70376 Stuttgart\n\nAnton Leo Rudolf Horn\nNeckartalstraße 127\n70376 Stuttgart",
    sec7Heading: "Dispute resolution",
    sec7Body:
      "We are neither willing nor obliged to take part in dispute-resolution proceedings before a consumer arbitration body. Authoritative version: German.",
  },
  datenschutz: {
    title: "Privacy policy",
    toc: "Contents",
    lastUpdated: "Last updated: September 24, 2026",
    sections: [
      {
        number: "1",
        title: "Controller",
        content: "0-AI UG (haftungsbeschränkt)\nNeckartalstraße 127\n70376 Stuttgart\nGermany\nEmail: datenschutz@cero-ai.com",
      },
      {
        number: "2",
        title: "Server logs",
        content: "When you visit our website, your browser automatically transmits technical information that may be stored in server logs.",
        list: [
          "Browser type and version",
          "Operating system used",
          "Referrer URL",
          "IP address of the accessing device",
          "Time of the server request",
        ],
        footer: "We use this information to deliver and secure the website, investigate errors, and prevent abuse.",
      },
      {
        number: "3",
        title: "Website analytics",
        content: "We operate a cookie-free analytics service at analytics.cero-ai.com. It records the hostname and path visited, referrer, screen dimensions, time, and technical request data. The service uses the IP address and user-agent transiently to calculate a one-way, pseudonymous visitor identifier; the raw IP address is not written to the analytics event, although it may appear in ordinary server logs. The analytics script does not use cookies or browser storage. Separately, if you select Allow analytics, we load Google Analytics to measure website page views. Google Ireland Limited may receive page, browser, and device data and use cookies or similar identifiers; Google LLC may process data in the United States. We do not load this Google tag before your consent. Your choice is stored in this browser and can be changed through Privacy choices. We use this optional measurement based on your consent under Art. 6(1)(a) GDPR and Section 25 TDDDG.",
      },
      {
        number: "4",
        title: "External fonts",
        content: "To display our typography, the website requests font styles and files from Google Fonts (Google) and Fontshare (Indian Type Foundry). When these resources load, the providers receive technical request data such as your IP address, browser information, requested resource, and time of access.",
      },
      {
        number: "5",
        title: "Contacting us",
        content: "If you contact us by email, we store the information you provide to answer the request and any follow-up questions. We share it only where necessary to handle your request or where the law requires us to do so.",
      },
      {
        number: "6",
        title: "Legal bases",
        content: "We process server-log and cookie-free analytics data under Art. 6 (1) lit. f GDPR, based on our legitimate interests in securely operating, understanding, and improving the website. Email enquiries are processed under Art. 6 (1) lit. b GDPR or, for general enquiries, Art. 6 (1) lit. f GDPR. External fonts are loaded under Art. 6 (1) lit. f GDPR for a consistent presentation of the website.",
      },
      {
        number: "7",
        title: "Service providers and international transfers",
        content: "We use external hosting providers and the services identified above to operate the website. Google and Fontshare may process data outside the EU/EEA. Where required, international transfers are based on an applicable adequacy decision, the European Commission's Standard Contractual Clauses, or another legally recognized safeguard. Provider privacy information is available on each provider's website.",
      },
      {
        number: "8",
        title: "Retention",
        content: "We retain server logs and rate-limit records only for the period needed for security and troubleshooting. Analytics records are kept only as long as needed to operate, understand, and improve the respective service, after which they are deleted or anonymized. Email correspondence is retained while needed to handle the request and meet legal retention duties. You may ask us to delete data associated with you by contacting datenschutz@cero-ai.com.",
      },
      {
        number: "9",
        title: "Your rights",
        content: "You have the following rights regarding your personal data:",
        rights: [
          ["Information", "Art. 15 GDPR"],
          ["Rectification", "Art. 16 GDPR"],
          ["Erasure", "Art. 17 GDPR"],
          ["Restriction of processing", "Art. 18 GDPR"],
          ["Data portability", "Art. 20 GDPR"],
          ["Objection", "Art. 21 GDPR"],
        ],
        rightPrefix: "Right to",
      },
      {
        number: "10",
        title: "Right to complain",
        content: "You have the right to complain to a data-protection supervisory authority about the processing of your personal data.",
      },
      {
        number: "11",
        title: "SSL/TLS encryption",
        content: "For security reasons this site uses SSL or TLS encryption. You can tell an encrypted connection by the address bar of your browser switching from http:// to https://.",
      },
    ],
  },
  meta: {
    "/": {
      title: "0-AI | We Rethink Web with AI",
      description: "We build AI and web products fast. Consulting, design and build for companies ready to ship AI-powered software.",
    },
    "/skyline/privacy": {
      title: "Skyline Privacy Policy | 0-AI",
      description: "Privacy policy for the Skyline iOS app: what data the app collects, how it is used, and your rights.",
    },
    "/skyline/terms": {
      title: "Skyline Terms of Service | 0-AI",
      description: "Terms governing use of the Skyline iOS app and community.",
    },
    "/skyline/support": {
      title: "Skyline Support | 0-AI",
      description: "Help with Skyline accounts, building collections, photo matching, safety, and privacy.",
    },
    "/products": {
      title: "Products. 0-AI",
      description: "CATE, Open CLI Deployment, Sight, Skyline Spotter and Foody. Products built by 0-AI.",
    },
    "/products/ai-overlay": {
      title: "AI Overlay | 0-AI",
      description: "A context-aware AI layer for your existing website, trained on your content and ready without a re-launch.",
    },
    "/services/ai-overlay": {
      title: "AI Overlay Services | 0-AI",
      description: "Add a context-aware AI layer to your existing website, trained on your content and integrated in one to two weeks.",
    },
    "/services/langzeit-betreuung": {
      title: "Long-term Partnership | 0-AI",
      description: "Ongoing AI strategy, maintenance, and product development with the team that built your solution.",
    },
    "/products/cate": {
      title: "CATE. Infinite canvas IDE for coding agents | 0-AI",
      description: "An infinite canvas IDE for parallel coding agents: agent-aware terminals, git worktrees, editors and browsers. Open source by 0-AI.",
    },
    "/products/one-click-deploy": {
      title: "Open CLI Deployment | 0-AI",
      description: "Open-source, CLI-first platform for deploying containers to your own servers with TLS, rollbacks and scaling. The alternative to Heroku, Railway and Render.",
    },
    "/consulting": {
      title: "AI Consulting | 0-AI",
      description: "Practical AI strategy and implementation support for teams building intelligent products.",
    },
    "/design": {
      title: "AI Product Design | 0-AI",
      description: "Design systems and interfaces for clear, useful AI-powered products.",
    },
    "/engineering": {
      title: "AI Engineering | 0-AI",
      description: "Full-stack AI engineering from prototype to reliable production systems.",
    },
    "/about": {
      title: "About. 0-AI",
      description: "We're 0-AI, a small studio helping companies adopt AI fast through consulting, design and engineering.",
    },
    "/contact": {
      title: "Contact. 0-AI",
      description: "Get in touch with 0-AI. Let's talk about your project.",
    },
    "/work": {
      title: "Projects. 0-AI",
      description: "Case studies and projects delivered by 0-AI for clients across industries.",
    },
    "/stack": {
      title: "Tech stack. 0-AI",
      description: "The technologies we use: Bun, React, TypeScript, Claude and more.",
    },
    "/blog": {
      title: "Blog. 0-AI",
      description: "Insights on AI development, product building and engineering from the 0-AI team.",
    },
    "/careers": {
      title: "Careers. 0-AI",
      description: "Join 0-AI. Open positions in engineering and design.",
    },
    "/open-source": {
      title: "Open source. 0-AI",
      description: "Our open-source projects and contributions to the developer community.",
    },
    "/pricing": {
      title: "Pricing. 0-AI",
      description: "Three packages for every size. Maintenance and AI layer optionally combinable.",
    },
    "/impressum": {
      title: "Imprint. 0-AI",
      description: "Legal notice and company information for 0-AI / 0-AI UG.",
    },
    "/datenschutz": {
      title: "Privacy. 0-AI",
      description: "Privacy policy for 0-AI / 0-AI UG.",
    },
    notFound: {
      title: "404. Page not found | 0-AI",
      description: "This page doesn't exist. But a little runner is waiting for you.",
    },
  },
  langSwitch: {
    aria: "Switch language",
    de: "DE",
    en: "EN",
  },
  ai: {
    newChat: "New chat",
    open: "Open",
    delete: "Delete",
    askPlaceholder: "Ask us anything.",
    rateLimited: "Daily limit reached, please come back tomorrow.",
    phaseLoading: "Processing.",
    phaseStreaming: "Generating.",
    failed: "Failed to generate response.",
    openAi: "Ask AI",
    sectionsOne: "section",
    sectionsMany: "sections",
    justNow: "Just now",
    minutesAgo: "{{n}} min ago",
    hoursAgo: "{{n}} h ago",
    daysAgoOne: "1 day ago",
    daysAgoMany: "{{n}} days ago",
    chatPlaceholderTitle: "Conversation",
    openContactPrompt: "Please show me a contact form so I can send you a message directly.",
    openPricingPrompt: "Please show me a clear pricing overview for CERO AI services and help me choose the right package.",
    pills: {
      "service-ai-overlay": "AI Overlay",
      "service-website-rework": "Website Rework",
      "service-langzeit-betreuung": "Long-term Partnership",
      "pricing": "Pricing",
      "our-process": "Our process",
      "about-us": "About",
      "open-source": "Open source",
    },
  },
  start: {
    cards: {
      overlay: {
        n: "01",
        label: "AI Overlay",
        tagline: "AI layer on existing site",
        desc: "Context-aware, trained on your content, just like this one. From €29/month.",
      },
      rework: {
        n: "02",
        label: "Website Rework: AI First",
        tagline: "Existing → AI-native",
        desc: "Redesign with AI as central interaction. Fixed price, fixed phases, predictable launch.",
      },
      longterm: {
        n: "03",
        label: "Long-term Partnership",
        tagline: "Direct line to devs",
        desc: "Advisory, maintenance and continued development with the same people who built it.",
      },
    },
    secondary: {
      about: { label: "About", desc: "Our studio & team" },
      cases: { label: "Cases & references", desc: "Selected projects" },
      opensource: { label: "Open source", desc: "Our projects on GitHub" },
    },
  },
  // Copy overrides used only when the AI_OVERLAY_ONLY gate is on. The site
  // stays a full agency site, but stops advertising website building/rework.
  overlayGate: {
    hero: {
      title: "AI layer and advisory for your website.",
      subtitle:
        "We build a context-aware AI layer on your existing site and stay with you long term. Direct collaboration with the people who also write the code.",
    },
    products: {
      lead: "Two core offerings, built for direct, long-term collaboration. You talk to the people who also code. No account-manager layer in between.",
    },
    pricing: {
      subtitle:
        "The context-aware AI layer for your existing website. Basic and Pro plans, or pay per answer. Prices include VAT.",
      teaserTitle: "AI layer. Clear price.",
      teaserLead: "Two plans or pay per answer. Full details and answer packs on the pricing page.",
      perMonth: "/month, 12-month term",
    },
  },
} as const;
